// PLACEHOLDER CONTENT — Replace with your actual Data Deletion draft.
// To update: edit the sections below or paste your draft HTML/text converted to JSX.

import { Mail } from "lucide-react";
import { motion } from "framer-motion";
import { usePageSeo } from "@/hooks/use-page-seo";
import { pageSeo } from "@/lib/seo";
import { fadeUp, stagger } from "@/lib/animations";

const DataDeletionPage = () => {
  usePageSeo(pageSeo.dataDeletion);
  return (
    <div className="min-h-screen">
      {/* ── Hero ── */}
      <section className="w-full border-b border-neutral-100 bg-gradient-to-b from-white to-stone-50 py-14">
        <motion.div
          className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8"
          variants={stagger}
          initial="hidden"
          animate="visible"
        >
          <motion.h1 variants={fadeUp} className="text-3xl font-bold text-neutral-900">Data Deletion Request</motion.h1>
          <motion.p variants={fadeUp} className="mt-3 text-sm text-neutral-500">
            Last updated: 20 May 2026
          </motion.p>
          <motion.p variants={fadeUp} className="mt-4 leading-relaxed text-neutral-600">
            You have the right to request deletion of your personal data that Arsoc
            holds. This page explains how to make that request and what to expect.
          </motion.p>
        </motion.div>
      </section>

      {/* ── Body ── */}
      <section className="w-full py-14">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <div className="space-y-10">

            <section>
              <h2 className="text-xl font-bold text-neutral-900">
                What data can be deleted
              </h2>
              <div className="mt-3 space-y-3 text-neutral-600">
                <p>
                  You may request deletion of any personal data that Arsoc holds about
                  you, including:
                </p>
                <ul className="ml-4 list-disc space-y-1.5">
                  <li>Account and profile information</li>
                  <li>Contact enquiry records</li>
                  <li>Any other personal data associated with your account or identity</li>
                </ul>
                <p>
                  Note that some data may be retained where we have a legal obligation to
                  do so (for example, financial records). We will inform you if this
                  applies to your request.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-bold text-neutral-900">
                How to request data deletion
              </h2>
              <div className="mt-3 space-y-3 text-neutral-600">
                <p>
                  To request deletion of your data, please contact us by email with the
                  subject line <strong>"Data Deletion Request"</strong> and include:
                </p>
                <ul className="ml-4 list-disc space-y-1.5">
                  <li>Your full name</li>
                  <li>The email address associated with your account or enquiry</li>
                  <li>
                    A brief description of the data you would like deleted (if you have
                    a specific request)
                  </li>
                </ul>
              </div>
            </section>

            <section className="rounded-xl border border-neutral-200 bg-stone-50 p-6">
              <h2 className="text-lg font-bold text-neutral-900">
                Send your deletion request
              </h2>
              <p className="mt-2 text-sm text-neutral-600">
                Email us directly to submit a data deletion request:
              </p>
              <a
                href="mailto:admin@arsoc.co.zw?subject=Data%20Deletion%20Request"
                className="mt-4 inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-white transition-opacity hover:opacity-90"
              >
                <Mail className="h-4 w-4" aria-hidden="true" />
                admin@arsoc.co.zw
              </a>
            </section>

            <section>
              <h2 className="text-xl font-bold text-neutral-900">What happens next</h2>
              <div className="mt-3 space-y-3 text-neutral-600">
                <p>
                  We will acknowledge your request within 5 business days. We aim to
                  complete all deletion requests within 30 days of receiving them, unless
                  the request is unusually complex, in which case we will inform you of
                  the expected timeline.
                </p>
                <p>
                  Once your data has been deleted, we will notify you by email. Where
                  data has been retained for legal reasons, we will explain which data
                  and why.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-bold text-neutral-900">
                WhatsApp and Alpha users
              </h2>
              <div className="mt-3 space-y-3 text-neutral-600">
                <p>
                  If you are an end-user who has interacted with a business via the
                  Alpha platform (through WhatsApp), your data is held by that business,
                  not by Arsoc directly. To request deletion of your data, you should
                  contact the business you interacted with.
                </p>
                <p>
                  If you are unable to identify or contact that business, please reach
                  out to us at{" "}
                  <a
                    href="mailto:admin@arsoc.co.zw"
                    className="text-primary underline underline-offset-2 hover:opacity-80"
                  >
                    admin@arsoc.co.zw
                  </a>{" "}
                  and we will assist you in identifying the appropriate controller.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-bold text-neutral-900">Further information</h2>
              <div className="mt-3 space-y-3 text-neutral-600">
                <p>
                  For more information about how we handle your personal data, please
                  read our{" "}
                  <a
                    href="/privacy-policy"
                    className="text-primary underline underline-offset-2 hover:opacity-80"
                  >
                    Privacy Policy
                  </a>
                  .
                </p>
                <address className="not-italic rounded-lg border border-neutral-100 bg-stone-50 p-4 text-sm">
                  <strong>Arsoc</strong><br />
                  150 Nelson Mandela Avenue, Harare, Zimbabwe<br />
                  <a
                    href="mailto:admin@arsoc.co.zw"
                    className="text-primary underline underline-offset-2 hover:opacity-80"
                  >
                    admin@arsoc.co.zw
                  </a>
                </address>
              </div>
            </section>

          </div>
        </div>
      </section>
    </div>
  );
};

export default DataDeletionPage;
